import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './Register.css';


const API_BASE = window.location.origin;


const PLAN_NAMES = {
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
};


function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const planName = PLAN_NAMES[planParam] || null;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    businessName: '',
    industry: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);


    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, plan: planName ? planParam : undefined })
      });


      const data = await res.json();


      if (!res.ok) {
        throw new Error(data.error || 'Registration failed');
      }


      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  if (success) {
    return (
      <div className="register-page">
        <div className="register-success">
          <div className="register-success-icon">✓</div>
          <h2>Account created!</h2>
          <p>Check your email to verify your account, then sign in to get started.</p>
          <Link to="/login" className="register-submit">Go to Login</Link>
        </div>
      </div>
    );
  }


  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-brand">
          <div className="register-brand-content">
            <h1 className="register-brand-logo">Chatflo</h1>
            <h2 className="register-brand-tagline">AI that works your leads while you work the job.</h2>
            <div className="register-brand-illustration">
              <div className="brand-chat-preview">
                <div className="brand-chat-msg brand-chat-msg--bot">
                  <span>Hi! Looking for a quote?</span>
                </div>
                <div className="brand-chat-msg brand-chat-msg--user">
                  <span>Yes, need my AC fixed ASAP</span>
                </div>
                <div className="brand-chat-msg brand-chat-msg--bot">
                  <span>Got it! I can book you for tomorrow at 2pm. Does that work?</span>
                </div>
              </div>
            </div>
            <ul className="register-brand-features">
              <li>24/7 instant lead response</li>
              <li>Automatic appointment booking</li>
              <li>SMS &amp; email follow-up</li>
            </ul>
          </div>
        </div>


        <div className="register-form-wrapper">
          <h2 className="register-title">Create your account</h2>
          <p className="register-subtitle">Start capturing every lead, 24/7.</p>


          {planName && (
            <div style={{
              background: '#f0f7f4',
              border: '1px solid #2E5E4E',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#1F4538',
            }}>
              You&apos;re starting the <strong>{planName}</strong> plan — includes a free 14-day pilot.
            </div>
          )}


          {error && (
            <div className="register-error">{error}</div>
          )}


          <form className="register-form" onSubmit={handleSubmit}>
            <div className="register-row">
              <div className="register-field">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="register-field">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>


            <div className="register-field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>


            <div className="register-field">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
              />
            </div>


            <div className="register-field">
              <label htmlFor="businessName">Business Name</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                required
              />
            </div>


            <div className="register-row">
              <div className="register-field">
                <label htmlFor="industry">Industry</label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="hvac">HVAC &amp; Plumbing</option>
                  <option value="roofing">Roofing &amp; Siding</option>
                  <option value="electrical">Electrical</option>
                  <option value="pest">Pest Control</option>
                  <option value="landscaping">Landscaping</option>
                  <option value="cleaning">Cleaning Services</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="register-field">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>


            <button type="submit" disabled={loading} className="register-submit">
              {loading ? (
                <span className="register-spinner" />
              ) : null}
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>


          <p className="register-switch">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}


export default Register;
